/* eslint-disable new-cap */
import { Settings } from "../../config";
import { QueryParam, EventType, ErrorCode } from "../../commons/Constants";
import Logger from "../../commons/Logger";
import {
  AdSense,
  getDefaultAdsOptions,
  getDefaultPageOptions,
  getDefaultRelatedLinkOptions,
  getDefaultSearchboxOptions,
} from "./adSenseUtils";
import { setupCallbackTracker } from "../AdSenseCallback";
import {
  getArrowPageLevel,
  getSimple2CAds,
  getSimple2CLightAds,
  getSimple2CLightPageLevel,
  getSimple2CLightRelatedLinks,
  getSimple2CRelatedLinks,
  getSimple2CSearchbox,
} from "../layout/Simple2C";
import { getParkWebPageLevel, getParkWebRelatedLinks } from "../layout/ParkWeb";
import { getHegPageLevel, getHegRelatedLinks } from "../layout/Heg";
import { isMobileBrowser } from "../../commons/HelperFunctions";
import { GoogleCaf } from "./consts";
import postEvent, { createBaseLanderEvent } from "../../commons/LanderEvent";

const adsenseConfig = {
  searchbox: {
    SIMPLE_2C: getSimple2CSearchbox(),
    SIMPLE_2C_LIGHT: getSimple2CSearchbox(),
    PARK_WEB: null,
    PARK_WEB_HEG: null,
  },
  relatedsearch: {
    SIMPLE_2C: getSimple2CRelatedLinks(),
    SIMPLE_2C_LIGHT: getSimple2CLightRelatedLinks(),
    PARK_WEB: getParkWebRelatedLinks(isMobileBrowser()),
    PARK_WEB_HEG: getHegRelatedLinks(),
  },
  ads: {
    SIMPLE_2C: getSimple2CAds(),
    SIMPLE_2C_LIGHT: getSimple2CLightAds(),
    PARK_WEB: null,
    PARK_WEB_HEG: null,
  },
  pagelevel: {
    SIMPLE_2C: null,
    SIMPLE_2C_LIGHT: getSimple2CLightPageLevel(),
    PARK_WEB: getParkWebPageLevel(),
    PARK_WEB_HEG: getHegPageLevel(),
    ARROW_1: getArrowPageLevel(),
    ARROW_2: getArrowPageLevel(),
    ARROW_3: getArrowPageLevel(),
  },
};

function getConfig(template, type) {
  try {
    return adsenseConfig[type][template];
  } catch (e) {
    Logger.error(
      "No case definition for block type " + type + ", template " + template
    );
  }
  return null;
}

// eslint-disable-next-line max-statements
export default function loadAdsense(
  landerConfig,
  queryConfig,
  page,
  setAdsenseSuccess
) {
  const lc = landerConfig;
  const qc = queryConfig;
  const template = lc.lander.template;

  const getSearchboxConfig = () => {
    return getConfig(template, GoogleCaf.blockType.SEARCH_BOX);
  };

  const getRelatedLinksConfig = () => {
    return getConfig(template, GoogleCaf.blockType.RELATED_SEARCH);
  };

  const getAdsConfig = () => {
    return getConfig(template, GoogleCaf.blockType.ADS);
  };

  // Page-Level Parameters as defined in the AdSense Custom Ad Frame (CAF) implementation guide and protocol reference
  // These options apply to all the CAF blocks on the landing page.
  const getPageLevelParams = () => {
    return adsenseConfig.pagelevel[lc.lander.template];
  };

  const handleRequestAccepted = (success) => {
    setAdsenseSuccess && setAdsenseSuccess(success);
  };

  // Setup callback and tracking
  setupCallbackTracker(lc, qc);

  // Create the caf options:
  // Merge the template-specific options with the default settings.
  // Template-specific settings (right hand side of the spread operator) will override default options.

  const pageOptions = {
    ...getDefaultPageOptions(lc, handleRequestAccepted),
    ...getPageLevelParams(),
  };
  let pageContentOptions;

  // Page content - Related link or Sponsored listings
  if (page === AdSense.page.RELATED_LINKS) {
    pageContentOptions = {
      ...getDefaultRelatedLinkOptions(lc, qc),
      ...getRelatedLinksConfig(),
    };
  } else {
    pageContentOptions = {
      ...getDefaultAdsOptions(lc, qc),
      ...getAdsConfig(),
    };
  }

  // Call Google caf library
  // Searchbox options must be passed as the last argument. 2 separate calls also works.
  if (typeof window.google !== "undefined") {
    if (qc[QueryParam.SEARCH_BOX]) {
      Logger.debug("Google CAF request with searchbox on");
      const searchboxOptions = {
        ...getDefaultSearchboxOptions(lc, qc),
        ...getSearchboxConfig(),
      };
      window.google.ads.domains.Caf(
        pageOptions,
        pageContentOptions,
        searchboxOptions
      );
    } else {
      Logger.debug("Google CAF request with searchbox off", pageContentOptions);
      window.google.ads.domains.Caf(pageOptions, pageContentOptions);
    }
  } else {
    Logger.info("window.google not found");
    const event = createBaseLanderEvent(EventType.ERROR, landerConfig);
    event.errorCode = ErrorCode.ERROR_ADSENSE_NO_CAF;
    postEvent(Settings.EVENT_PUBLISH_API, event);
    handleRequestAccepted(false);
  }
}
